import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { Icon, makeStyles, Theme, FormControl, FormLabel, Checkbox, FormControlLabel, Button, FormGroup, FormHelperText, Paper }
    from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { capitalizeFirst, TimeSeriesType, useModeFormStyle, useModePanelStyle, getErrorMessage, SlideOutComp, GenericMessageBox }
    from '@moderepo/mode-app-components';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import produce, { Draft } from 'immer';
import { hideSlideOutPanel, modeUIContext, setIsEditingForm, showErrorNotification, showSuccessNotification } from '@moderepo/mode-ui-state';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ApiError, AppAPI } from '@moderepo/mode-apis';
import moment from 'moment';
import { useRitzTranslation } from '../../utils/customHooks';
import { DownloadableDataInfo, TSDownloadDataRequestManager } from './TSDownloadDataRequestManager';



const useStyle = makeStyles((theme: Theme) => {
    return {
        content: {
            [theme.breakpoints.up('md')]: {
                minWidth: 600,
            },
        },

        title: {
            fontWeight: 500,
            fontSize  : 'larger',
        },

        formControl: {
            marginBottom: theme.spacing(2),
            width       : '100%',
        },

        formControlLabel: {
            paddingBottom: theme.spacing(1),
        },

        formGroup: {
            paddingLeft: theme.spacing(2),
        },

        dateSelectors: {
            flexDirection: 'row',
            alignItems   : 'center',
        },

        selectDataInputField: {
            border    : '1px solid #ff0000',
            '& select': {
                minHeight: 200,
            },
        },

        datesSeparator: {
            margin: theme.spacing(0, 1),
        },

        selectAllButton: {
            width     : 'fit-content',
            marginLeft: theme.spacing(2),
        },
    };
});

interface InputErrors {
    data?: string;
    dateRange?: string;
}


// The maximum length of the file name the backend support INCLUDING extension
const MAX_FILE_NAME_LENGTH = 63;

// The set of characters backend does not allow
const RESTRICTED_FILE_NAME_CHARS = ['\\', '/', ':'];


/**
 * Fix the list of file names and return the modified file names. There are a few steps we need do
 *  - Convert all restricted characters with _
 *  - Make sure no duplicate file names. If there are, add a number to the end of the file name
 *  - Make sure the file names are no longed than the max length
 */
const fixFileNames = (fileNames: readonly string[], extension: string): string[] => {
    // Make a copy of the fileNames since fileNames is readonly
    const newFileNames = [...fileNames];

    // Since we will add extension at the end, we need to make sure the newFileName is not longer than the max file name length WITHOUT extension
    // And there is a chance we will add numbers to the end of the file name to fix duplication, we need to make it 3 char shorter. We will add
    // these to the file name e.g.  '_01', '_02', ..., '_99'. Hopefully we won't have more than 99 duplicate file names. If we do, we need to cut more
    const maxLengthWithoutExtension = MAX_FILE_NAME_LENGTH - (extension.length + 1) - 3;    // (extension.length + 1) is because include dot '.csv'

    // For each file name, convert restricted characters to _ and at the same time, compile a map of duplicate file names
    const duplicateFileNameCounter = {};
    newFileNames.forEach((fileName: string, index: number) => {

        // Make a copy of the file name and trim it
        let fixedFileName = fileName.trim();

        // Replace restricted chars with _
        RESTRICTED_FILE_NAME_CHARS.forEach((restrictedChar) => {
            fixedFileName = fixedFileName.replaceAll(restrictedChar, '_');
        });

        // Make sure the file name's length is less than max length
        if (fixedFileName.length > maxLengthWithoutExtension) {
            fixedFileName = fixedFileName.substr(0, maxLengthWithoutExtension);
        }

        // Replace the fileName at index with the fixedFileName
        newFileNames[index] = fixedFileName;

        // Count the number of duplicate for EACH file name. We will use this later to fix duplicate
        if (!duplicateFileNameCounter[fixedFileName]) {
            duplicateFileNameCounter[fixedFileName] = 1;    // If this is the first time we see the file name, set the counter to 1
        } else {
            // eslint-disable-next-line operator-assignment
            duplicateFileNameCounter[fixedFileName] = duplicateFileNameCounter[fixedFileName] + 1;
        }
    });


    // Now we need to fix duplicate file names
    // Each time we add '_##' e.g. '_01' to the end of a duplicate file name, we need to keep track of the counter so that the next time we
    // see the file name again, we need to increment it and add '_02'. Therefore we need this object to keep track of the counter for EACH file name
    const fixedDuplicateFileNameCounter = {};

    newFileNames.forEach((fileName: string, index: number) => {
        if (duplicateFileNameCounter[fileName] > 1) {
            // This fileName appeared multiple times. We need to add a number to the end of the file name

            // Get the duplicate file name count or initialize it with 0 if we haven't see it yet. Then increment it by 1
            let count = fixedDuplicateFileNameCounter[fileName] ?? 0;
            count += 1;

            // Add the count to the end of the fileName and then replace the fileName at index with the new name
            newFileNames[index] = `${fileName}_${count.toString().padStart(2, '0')}`;

            // Save the count
            fixedDuplicateFileNameCounter[fileName] = count;
        }
    });


    // Add extension to each file name and return the file names
    return newFileNames.map((fileName) => {
        return `${fileName}.${extension}`;
    });
};



export interface TSDownloadDataRequestPreviewContainerProps {
    readonly projectId: number;
    readonly homeId: number;
    readonly tsdbSmartModuleId: string;
    readonly downloadableDataInfo: readonly DownloadableDataInfo[];
    readonly startDate: number;
    readonly endDate: number;
    readonly dateBounds: {
        readonly minDate: number;
        readonly maxDate: number;
    }
}


/**
 * This container ise used for displaying a Device's Alert status
 */
export const TSDownloadDataRequestPreviewContainer = (props: TSDownloadDataRequestPreviewContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { projectId } = props;
    const project = selectProjectById(dataState, projectId);

    const { trans } = useRitzTranslation(project);

    // A map of selected data which the key is the data ID and the value is true/false to indicate if the data is selected
    const [selectedDataMap, setSelectedDataMap] = useState<{
        [dataId: string]: boolean
    }>(props.downloadableDataInfo.reduce((result, dataInfo: DownloadableDataInfo) => {
        return {
            ...result,
            [dataInfo.id]: Boolean(dataInfo.selected),
        };
    }, {}));

    const [selectedDateRange, setSelectedDateRange] = useState<{
        readonly startDate: number;
        readonly endDate: number;
    }>({
        startDate: props.startDate,
        endDate  : props.endDate,
    });

    const [inputErrors, setInputErrors] = useState<InputErrors>({});

    const classes = useStyle();
    const formClasses = useModeFormStyle();
    const panelClasses = useModePanelStyle();


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));
    }, [uiDispatch]);


    useEffect(() => {
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);


    /**
     * Unselected the current selected Alert entity
     */
    const hidePreviewPanel = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
        onChangeEditingMode(false);
    }, [onChangeEditingMode, uiDispatch]);


    /**
     * Toggle one of the data's selection. Also, check if any of the data is selected. If not, show error.
     */
    const onToggleDataSelection = useCallback((dataId: string, value: boolean) => {
        const newSelectedDataMap = {
            ...selectedDataMap,
            [dataId]: value,
        };
        setSelectedDataMap(newSelectedDataMap);

        // Check if there is at least 1 data selected
        const hasSelectedData = Object.values(newSelectedDataMap).some((selected: boolean) => {
            return selected;
        });

        if (hasSelectedData) {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.data = undefined;
            }));
        } else {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.data = capitalizeFirst(trans('panel.ts_download_request.no_selection.error'));
            }));
        }
    }, [inputErrors, selectedDataMap, trans]);


    /**
     * On start date/time selected
     */
    const onStartTimeChange = useCallback((value: MaterialUiPickersDate) => {
        if (value && props.dateBounds) {
            const timestamp = value.valueOf();
            setSelectedDateRange(produce(selectedDateRange, (draft: any) => {
                draft.startDate = timestamp;
            }));

            // make sure the new value is less than the current selected endDate
            if (timestamp >= selectedDateRange.endDate) {
                setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                    draft.dateRange = capitalizeFirst(trans('panel.ts_download_request.date_range.error'));
                }));
            } else {
                setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                    draft.dateRange = undefined;
                }));
            }
        }
    }, [inputErrors, props.dateBounds, selectedDateRange, trans]);


    /**
     * On end date/time selected
     */
    const onEndTimeChange = useCallback((value: MaterialUiPickersDate) => {
        if (value && props.dateBounds) {
            const timestamp = value.valueOf();
            setSelectedDateRange(produce(selectedDateRange, (draft: any) => {
                draft.endDate = timestamp;
            }));

            // make sure the new value is greater than the current selected start date
            if (timestamp <= selectedDateRange.startDate) {
                setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                    draft.dateRange = capitalizeFirst(trans('panel.ts_download_request.date_range.error'));
                }));
            } else {
                setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                    draft.dateRange = undefined;
                }));
            }
        }
    }, [inputErrors, props.dateBounds, selectedDateRange, trans]);



    const onDownloadRequestFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();


        // filter out data that are not selected
        const selectedData = props.downloadableDataInfo.filter((dataInfo: DownloadableDataInfo) => {
            return selectedDataMap[dataInfo.id];
        });

        // For the selected data, get the list of simeSeries and simpleSeriesIds
        const simpleSeries = selectedData.filter((dataInfo: DownloadableDataInfo) => {
            return dataInfo.type === TimeSeriesType.SIMPLE;
        });
        const selectedSimpleSeriesIds = simpleSeries.map((dataInfo: DownloadableDataInfo) => {
            return dataInfo.id;
        });

        // For the selected data, get the list of collections and collectionIds
        const collections = selectedData.filter((dataInfo: DownloadableDataInfo) => {
            return dataInfo.type === TimeSeriesType.COLLECTION;
        });
        const selectedCollectionIds = collections.map((dataInfo: DownloadableDataInfo) => {
            return dataInfo.id;
        });

        // We have either simpleSeries or collections but not both. So use one of the list of DownloadableDataInfo array that has length > 0
        // and get the fileNames for that list
        const dataInfoToBeExported = simpleSeries.length > 0 ? simpleSeries : collections;

        // If we are going to pass file names to the backend, we need to have name for ALL items or none. For example, if there are 10
        // items to export, we can't pass only 6 fileNames, we have to pass all 10. So we need to make sure all the dataInfo contain name.
        // If only some of them has name then we will not pass custom file name to backend and have backend use default names
        const dataInfosWithName = dataInfoToBeExported.filter((dataInfo) => {
            return dataInfo.exportFileName;
        });

        const fileNames = dataInfosWithName.length === dataInfoToBeExported.length
            // All dataInfo to be exported has name so we can create an array of custom name
            ? fixFileNames(dataInfosWithName.map((dataInfo: DownloadableDataInfo) => {
                return String(dataInfo.exportFileName);
            }), 'csv')
            : undefined;        // We don't have enough dataInfo with name so pass undefined for fileNames


        try {
            const startDate = moment(selectedDateRange.startDate).startOf('minutes').format('YYYY-MM-DDTHH:mm:ssZ');
            const endDate = moment(selectedDateRange.endDate).startOf('minutes').format('YYYY-MM-DDTHH:mm:ssZ');

            const exportInfo = await AppAPI.getInstance().exportTimeSeriesData(
                props.homeId, props.tsdbSmartModuleId, selectedSimpleSeriesIds, selectedCollectionIds, startDate, endDate, true, undefined, fileNames,
            );

            // result contains a couple of URLs, 1 url to check the request status and 1 url to download the data. Depending on how much data
            // is requested, these URLs might not be accessible right away. The backend will upload the requested data to S3 and when it is done,
            // the status URL will return with status "SUCCESS" and the download data URL will be downloadable. This can be a second long or can
            // be hours long. So we can't make the user wait until the data is ready to be downloaded.
            // So the solution is to keep track of the requests using the TSDownloadRequestManager and create a page where the user can go to check
            // their status and download the data when they are ready. This will allow the user to get back to the App and do what they need to do.
            if (exportInfo) {
                TSDownloadDataRequestManager.getInstance().addRequest(
                    props.projectId, props.homeId, props.tsdbSmartModuleId, selectedData, { start: startDate, end: endDate }, exportInfo,
                );

                hidePreviewPanel();
                uiDispatch(showSuccessNotification('notif.ts_download_request.sent', undefined, 10000));
            } else {
                uiDispatch(showErrorNotification('error.message.unknown'));
            }
        } catch (error) {
            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
        }

    }, [props.downloadableDataInfo, props.homeId, props.tsdbSmartModuleId, props.projectId, selectedDataMap, selectedDateRange.startDate,
        selectedDateRange.endDate, hidePreviewPanel, uiDispatch]);


    /**
     * On selectAll clicked. We will select all data or unselect all data based on 'value'
     */
    const toggleSelectAll = useCallback((value: boolean) => {
        const newSelectedDataMap = props.downloadableDataInfo.reduce((result, dataInfo: DownloadableDataInfo) => {
            return {
                ...result,
                [dataInfo.id]: value,
            };
        }, {});
        setSelectedDataMap(newSelectedDataMap);

        // Update inputErrors.data accordingly based on whether we selectedAll or unselectAll
        if (value) {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.data = undefined;
            }));
        } else {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.data = capitalizeFirst(trans('panel.ts_download_request.no_selection.error'));
            }));
        }
    }, [inputErrors, props.downloadableDataInfo, trans]);


    // Check if all data are selected so that we can show the "Select All" and "Unselect All" options accordingly
    const areAllSelected = useMemo(() => {
        return Object.values(selectedDataMap).filter((value: boolean) => {
            return value === true;
        }).length >= props.downloadableDataInfo.length;
    }, [props.downloadableDataInfo.length, selectedDataMap]);


    return (
        <SlideOutComp
            compContent={(
                <Paper className={clsx(panelClasses.root, 'transparent', 'editing')}>
                    <div className={clsx(panelClasses.panelContent)}>
                        <form className={clsx(formClasses.root)} noValidate autoComplete="on" onSubmit={onDownloadRequestFormSubmit}>
                            {props.downloadableDataInfo.length > 0 ? (
                                <div className={formClasses.formInputGroup}>
                                    <FormControl
                                        required
                                        error={Boolean(inputErrors.data)}
                                        className={classes.formControl}
                                    >
                                        <div>
                                            <FormLabel
                                                className={classes.formControlLabel}
                                            >{capitalizeFirst(trans('panel.ts_download_request.select_data.label'))}
                                            </FormLabel>

                                            {/* Only show 'Select All' options if there are more than 5 data to select */}
                                            {props.downloadableDataInfo.length > 1 && (
                                                <Button
                                                    className={classes.selectAllButton}
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => {
                                                        toggleSelectAll(!areAllSelected);
                                                    }}
                                                >
                                                    {capitalizeFirst(trans(areAllSelected ? 'label.unselect_all' : 'label.select_all'))}
                                                </Button>
                                            )}
                                        </div>

                                        <FormGroup className={classes.formGroup}>
                                            {props.downloadableDataInfo.map((dataInfo: DownloadableDataInfo) => {
                                                return (
                                                    <FormControlLabel
                                                        key={dataInfo.id}
                                                        control={(
                                                            <Checkbox
                                                                value={selectedDataMap[dataInfo.id]}
                                                                checked={selectedDataMap[dataInfo.id]}
                                                                color="primary"
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                    onToggleDataSelection(dataInfo.id, event.target.checked);
                                                                }}
                                                            />
                                                        )}
                                                        label={dataInfo.name || dataInfo.id}
                                                        labelPlacement="end"
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                        <FormHelperText>{inputErrors.data}</FormHelperText>
                                    </FormControl>

                                    <FormControl
                                        required
                                        className={classes.formControl}
                                        error={Boolean(inputErrors.dateRange)}
                                    >
                                        <FormLabel
                                            className={classes.formControlLabel}
                                        >{capitalizeFirst(trans('panel.ts_download_request.select_date_range.label'))}
                                        </FormLabel>
                                        <FormGroup className={clsx(classes.formGroup, classes.dateSelectors)}>
                                            <KeyboardDateTimePicker
                                                label={capitalizeFirst(trans('label.start_date'))}
                                                allowKeyboardControl
                                                minDate={props.dateBounds.minDate}
                                                maxDate={selectedDateRange.endDate}
                                                value={selectedDateRange.startDate}
                                                format="YYYY/MM/DD HH:mm"
                                                ampm={false}
                                                todayLabel={capitalizeFirst(trans('label.today'))}
                                                okLabel={capitalizeFirst(trans('label.ok'))}
                                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                                showTodayButton
                                                disableFuture
                                                onChange={onStartTimeChange}
                                            />
                                            <Icon className={classes.datesSeparator} fontSize="small">forward</Icon>
                                            <KeyboardDateTimePicker
                                                label={capitalizeFirst(trans('label.end_date'))}
                                                allowKeyboardControl
                                                minDate={selectedDateRange.startDate}
                                                maxDate={props.dateBounds.maxDate}
                                                value={selectedDateRange.endDate}
                                                format="YYYY/MM/DD HH:mm"
                                                ampm={false}
                                                todayLabel={capitalizeFirst(trans('label.today'))}
                                                okLabel={capitalizeFirst(trans('label.ok'))}
                                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                                showTodayButton
                                                disableFuture
                                                onChange={onEndTimeChange}
                                            />
                                        </FormGroup>
                                        <FormHelperText>{inputErrors.dateRange}</FormHelperText>
                                    </FormControl>
                                </div>
                            ) : (
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: trans('label.time_series_data', { count: 2 }),
                                    })}
                                />
                            )}
                            <div className={clsx(formClasses.formActionContainer)}>
                                <div />
                                <div>
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        onClick={() => {
                                            hidePreviewPanel();
                                        }}
                                    >
                                        {trans('label.cancel')}
                                    </Button>
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        disabled={Boolean(props.downloadableDataInfo.length === 0 || inputErrors.data || inputErrors.dateRange)}
                                    >
                                        {trans('label.download_time_series_data')}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                </Paper>
            )}
            closable={false}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
